import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Grid,
  alpha,
  IconButton,
  Tooltip,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import VpnKey from '@mui/icons-material/VpnKey';
import School from '@mui/icons-material/School';
import PersonOutline from '@mui/icons-material/PersonOutline';
import People from '@mui/icons-material/People';
import GroupWork from '@mui/icons-material/GroupWork';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Storefront from '@mui/icons-material/Storefront';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, LoginFormData } from '../../schemas';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface LoginPageProps {
  onLoginSuccess: () => void;
  onNavigate?: (path: string) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, onNavigate }) => {
  const { login } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: 'emma.johnson@school.edu',
      password: 'password123',
      role: 'student',
    },
  });

  const selectedRole = watch('role');

  // Auto-populate the email field with a demo account whenever the
  // user toggles to a different role — keeps the "click → sign in"
  // flow one step instead of two. We only overwrite the email if it
  // currently matches one of the demo addresses (so a user who has
  // started typing their own email doesn't lose their input).
  useEffect(() => {
    const demoEmailFor: Record<string, string> = {
      student: 'emma.johnson@school.edu',
      parent: 'lisa.johnson@email.com',
      community: 'james.anderson@email.com',
    };
    const knownDemoEmails = Object.values(demoEmailFor).concat([
      'michael.johnson@email.com',
      'patricia.martinez@email.com',
    ]);
    const next = demoEmailFor[selectedRole];
    if (!next) return;
    const current = getValues('email');
    if (!current || knownDemoEmails.includes(current)) {
      setValue('email', next, { shouldValidate: false });
    }
  }, [selectedRole, setValue, getValues]);

  const onSubmit = async (data: LoginFormData) => {
    setError('');
    setLoading(true);

    try {
      await login(data.email, data.password, data.role);
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setSnackbarOpen(true);
  };

  const getRoleInfo = (role: UserRole) => {
    switch (role) {
      case 'student':
        return {
          icon: <PersonOutline sx={{ fontSize: 40 }} />,
          title: 'Student Login',
          description: 'Access your pre-assessed fees and browse available items',
        };
      case 'parent':
        return {
          icon: <People sx={{ fontSize: 40 }} />,
          title: 'Parent Login',
          description: 'Manage fees for your children and make payments',
        };
      case 'community':
        return {
          icon: <GroupWork sx={{ fontSize: 40 }} />,
          title: 'Community Access',
          description: 'Purchase event tickets and other public items',
        };
    }
  };

  const roleInfo = getRoleInfo(selectedRole);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#f3f4f6',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 2, sm: 3 },
            animation: 'fadeIn 0.5s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(20px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
              py: { xs: 4, sm: 5, md: 6 },
              px: { xs: 3, sm: 4 },
              textAlign: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: '50%',
                bgcolor: 'white',
                mb: { xs: 2, sm: 3 },
              }}
            >
              <School sx={{ fontSize: { xs: 36, sm: 48 }, color: '#0f766e' }} />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={700}
              sx={{
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}
            >
              ClearCollect
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Manage your school fees and payments
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'white' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                  Select Your Role
                </Typography>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      {...field}
                      exclusive
                      fullWidth
                      data-tour="login-role-tabs"
                      sx={{
                        mb: 2,
                        '& .MuiToggleButton-root': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          py: { xs: 1, sm: 1.5 }
                        }
                      }}
                    >
                      <ToggleButton value="student">Student</ToggleButton>
                      <ToggleButton value="parent">Parent</ToggleButton>
                      <ToggleButton value="community">Community</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: alpha('#0f766e', 0.04),
                    borderColor: alpha('#0f766e', 0.2),
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ '& svg': { fontSize: { xs: 32, sm: 40 } } }}>
                      {roleInfo.icon}
                    </Box>
                    <Typography variant="h6" sx={{ ml: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }} fontWeight={600}>
                      {roleInfo.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {roleInfo.description}
                  </Typography>
                </Paper>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 2 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Address"
                      type="email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Password"
                      type="password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Box>

              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mb: 2,
                  border: theme => `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    minHeight: 44,
                    '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
                  }}
                >
                  <VpnKey sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={600}>
                    Show demo credentials
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" display="block">
                          Student: emma.johnson@school.edu
                        </Typography>
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard('emma.johnson@school.edu')}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" display="block">
                          Parent (1 child): michael.johnson@email.com
                        </Typography>
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard('michael.johnson@email.com')}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" display="block">
                          Parent (2 children): lisa.johnson@email.com
                        </Typography>
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard('lisa.johnson@email.com')}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" display="block">
                          Community: james.anderson@email.com
                        </Typography>
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard('james.anderson@email.com')}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" display="block">
                          Password: password123
                        </Typography>
                        <Tooltip title="Copy password">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard('password123')}
                            sx={{ ml: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              {selectedRole === 'parent' && onNavigate && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Button
                      onClick={() => onNavigate('/register')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Register as Parent
                    </Button>
                  </Typography>
                </Box>
              )}

              {selectedRole === 'community' && onNavigate && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Button
                      onClick={() => onNavigate('/register-community')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Register as Community Member
                    </Button>
                  </Typography>
                </Box>
              )}
            </form>

            {onNavigate && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
                    or
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="text"
                    fullWidth
                    size="medium"
                    startIcon={<AdminPanelSettings />}
                    onClick={() => onNavigate('/admin')}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha('#0f172a', 0.05),
                      },
                    }}
                  >
                    Admin Portal
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    size="medium"
                    startIcon={<Storefront />}
                    onClick={() => onNavigate('/pos')}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha('#f59e0b', 0.1),
                      },
                    }}
                  >
                    POS Terminal
                  </Button>
                </Box>
              </>
            )}

          </Box>
        </Paper>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
